import React from 'react';
import pick from 'lodash.pick';

import { FREEMIUM_EVENT_TUTORIAL_STEPS } from './constants';
import {
  ALBUM_SUBTYPE,
  ALBUM_TYPE,
  ALBUM_UPLOAD_PERMISSIONS,
} from '../../../../../constants/albums';
import FreemiumEventCreateAlbumsStep from '../FreemiumEventCreateWizard/components/FreemiumEventCreateAlbumsStep';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../../constants/event-items';
import FreemiumEventCreateJoinCodeStep from '../FreemiumEventCreateWizard/components/FreemiumEventCreateJoinCodeStep';
import FreemiumEventCreateSocialStep from '../FreemiumEventCreateWizard/components/FreemiumEventCreateSocialStep';
import {
  generateMachineJoinCode,
  isValidJoinCode,
} from '../../../../../util/join-code';
import { isAlbumFolder } from '../../../../../util/album-folder';
import FreemiumEventTutorialGroupVideoStep from './components/FreemiumEventTutorialGroupVideoStep';
import MarketingMaterialsShare from '../../../components/ContributorShare/components/MarketingMaterialsShare/MarketingMaterialsShare';
import { joinCodeSearch } from '../../../../web-gallery/views/AlbumSearch/graphql';
import { getErrorString } from '../../../../../util/normalize-errors';
import { getWatermarkConfigInput } from '../../Album/AlbumSettings/AlbumBrandFilterSettings/helpers';
import ShutterbugShare from '../../../components/ContributorShare/components/ShutterbugShare/ShutterbugShare';
import { STRIPE_CHECKOUT_MODAL } from '../../../../../components/global/StripeCheckout';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../../constants/account-referrals';
import { isSpecialtyAlbum, isVGBContext } from '../../../../../util/album';
import { getEventAddonItemsByType } from '../../../components/FreemiumEventPurchase/helpers';
import PriceDisplay from '../../../../../components/global/PriceDisplay';
import { canPurchaseEventAddon } from '../../../../../util/product-permissions';
import FreemiumEventTutorialWaldofiedUpsellStep from './components/FreemiumEventTutorialWaldofiedUpsellStep';
import FreemiumEventTutorialMemoryBookStep from './components/FreemiumEventTutorialMemoryBookStep';
import { postNativeAppFreemiumEventTutorialConfigEventAddonItemPurchase } from '../../../../../services/native-bridge/native-apps-interface';
import {
  CONTRIBUTOR_UPLOAD_ACCESS,
  CONTRIBUTOR_VIEW_ACCESS,
} from '../../../../../constants/contributors';
import FreemiumEventTutorialPermissionsStep from './components/FreemiumEventTutorialPermissionsStep/FreemiumEventTutorialPermissionsStep';

export const getEventTutorialSteps = ({ stepProps = {}, setStepProps }) => {
  const { entity, eventAddonItems, purchasedAddonItems } = stepProps;

  const isAlbumFolderEntity = isAlbumFolder(entity);
  const albumId = isAlbumFolderEntity ? undefined : entity.id;
  const albumFolderId = isAlbumFolderEntity
    ? entity?.id
    : entity?.root_album_folder?.id;

  const [groupVideoEventAddon] = getEventAddonItemsByType(
    eventAddonItems,
    EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
  );
  const [memoryBookEventAddon] = getEventAddonItemsByType(
    eventAddonItems,
    EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
  );
  const [waldofiedEventAddon] = getEventAddonItemsByType(
    eventAddonItems,
    EVENT_ADDON_ITEM_TYPE.WALDOFIED,
  );

  const isVGB = isVGBContext(entity);
  const groupVideoPurchased = !!purchasedAddonItems.find(
    purchasedAddonItem =>
      purchasedAddonItem.event_addon_type === EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
  );
  const memoryBookPurchased = !!purchasedAddonItems.find(
    purchasedAddonItem =>
      purchasedAddonItem.event_addon_type === EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
  );

  const waldofiedPurchased = !!purchasedAddonItems.find(purchasedAddonItem =>
    [EVENT_ADDON_ITEM_TYPE.WALDOFIED].includes(
      purchasedAddonItem.event_addon_type,
    ),
  );

  return {
    [FREEMIUM_EVENT_TUTORIAL_STEPS.PHOTO_ALBUMS]: {
      label: 'Photo albums',
      key: FREEMIUM_EVENT_TUTORIAL_STEPS.PHOTO_ALBUMS,
      title: 'Which photo albums would you like to create?\n',
      alertLabel: 'Add more photo albums',
      skipBtnLabel: 'Skip',
      actionBtnLabel: 'Add now',
      modalSkipBtnLabel: 'Skip',
      layoutSize: 'lg',
      skip: entity?.album_subtype !== ALBUM_SUBTYPE.WEDDINGS,
      component: props => {
        return (
          <>
            <FreemiumEventCreateAlbumsStep
              stepProps={stepProps}
              setStepProps={setStepProps}
              {...props}
            />
          </>
        );
      },
    },

    [FREEMIUM_EVENT_TUTORIAL_STEPS.GROUP_VIDEO_SETUP]: {
      title: isVGB
        ? 'A guestbook that really speaks to us'
        : 'Gather videos from everyone',
      key: FREEMIUM_EVENT_TUTORIAL_STEPS.GROUP_VIDEO_SETUP,
      alertLabel: `Set up a ${isVGB ? 'Video Guestbook' : 'Group Video'}`,
      skipBtnLabel: groupVideoPurchased ? 'All done' : 'Skip',
      actionBtnLabel: groupVideoPurchased ? null : 'Add now',
      modalActionBtnLabel: groupVideoPurchased ? (
        'Done'
      ) : (
        <>
          <span className="mr-1">Add now</span>
          <PriceDisplay
            price={groupVideoEventAddon?.price?.value}
            currency={groupVideoEventAddon?.price?.currency}
            trimDecimalZeros={false}
          />
        </>
      ),
      modalSkipBtnLabel: groupVideoPurchased
        ? "I'm done"
        : "I'm not interested",
      alertActionBtnProps: {
        id: 'onboarding-vgb-banner-action',
      },
      alertSkipBtnProps: {
        id: 'onboarding-vgb-banner-skip',
      },
      modalActionBtnProps: {
        id: 'onboarding-vgb-action',
      },
      modalSkipBtnProps: {
        id: 'onboarding-vgb-skip',
      },
      layoutSize: 'lg',
      skip:
        !canPurchaseEventAddon(
          EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
          entity.album_subtype,
        ) ||
        entity?.purchased_event_addon_types?.includes(
          EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
        ),
      component: props => {
        return (
          <>
            <FreemiumEventTutorialGroupVideoStep
              stepProps={stepProps}
              setStepProps={setStepProps}
              {...props}
            />
          </>
        );
      },
    },
    [FREEMIUM_EVENT_TUTORIAL_STEPS.PERMISSIONS]: {
      label: 'Permissions',
      title: "Review your event's permissions",
      alertLabel: "Review your event's permissions",
      skipBtnLabel: 'Skip',
      actionBtnLabel: 'Review now',
      modalActionBtnLabel: 'Save and continue',
      modalSkipBtnLabel: 'Skip',
      key: FREEMIUM_EVENT_TUTORIAL_STEPS.PERMISSIONS,
      layoutSize: 'sm',
      skippable: () => {
        return true;
      },
      component: props => {
        return (
          <>
            <FreemiumEventTutorialPermissionsStep
              stepProps={stepProps}
              setStepProps={setStepProps}
              {...props}
            />
          </>
        );
      },
    },
    [FREEMIUM_EVENT_TUTORIAL_STEPS.QR_CODE_DOWNLOAD]: {
      key: FREEMIUM_EVENT_TUTORIAL_STEPS.QR_CODE_DOWNLOAD,
      title: 'Get easy QR code and SMS joining for your guests!',
      alertLabel:
        'Access print-ready QR code signage and SMS joining for your guests',
      skipBtnLabel: 'Maybe later',
      actionBtnLabel: 'Access now',
      modalActionBtnLabel: waldofiedPurchased ? (
        "I've downloaded my QR signs"
      ) : (
        <>
          <span className="mr-1">Add now</span>
          <PriceDisplay
            price={waldofiedEventAddon?.price?.value}
            currency={waldofiedEventAddon?.price?.currency}
            trimDecimalZeros={false}
          />
        </>
      ),
      modalSkipBtnLabel: 'Maybe later',
      layoutSize: 'lg',
      alertActionBtnProps: {
        id: 'onboarding-gold-banner-action',
      },
      alertSkipBtnProps: {
        id: 'onboarding-gold-banner-skip',
      },
      modalActionBtnProps: {
        id: 'onboarding-gold-action',
      },
      modalSkipBtnProps: {
        id: 'onboarding-gold-skip',
      },
      component: props => {
        if (!waldofiedPurchased) {
          return (
            <>
              <FreemiumEventTutorialWaldofiedUpsellStep
                stepProps={stepProps}
                setStepProps={setStepProps}
                waldofiedEventAddon={waldofiedEventAddon}
                {...props}
              />
            </>
          );
        }

        return (
          <>
            <MarketingMaterialsShare
              albumFolderId={albumFolderId}
              albumId={albumId}
              headless
              footerless
              shareable={false}
            />
          </>
        );
      },
      skippable: () => {
        return true;
      },
    },

    [FREEMIUM_EVENT_TUTORIAL_STEPS.JOIN_CODE]: {
      label: 'Join Code',
      title: 'Create a unique join code for your guests!',
      alertLabel: 'Create a unique join code for your guests',
      skipBtnLabel: 'Skip',
      actionBtnLabel: 'Add now',
      modalActionBtnLabel: 'Continue',
      modalSkipBtnLabel: 'Skip',
      key: FREEMIUM_EVENT_TUTORIAL_STEPS.JOIN_CODE,
      layoutSize: 'lg',
      skip: !entity?.purchased_event_addon_types?.includes(
        EVENT_ADDON_ITEM_TYPE.WALDOFIED,
      ),
      component: props => {
        return (
          <>
            <FreemiumEventCreateJoinCodeStep
              stepProps={stepProps}
              setStepProps={setStepProps}
              {...props}
            />
          </>
        );
      },
      validate: async (values, apolloClient) => {
        const errors = {};

        if (values.join_code && values.join_code !== values.join_code_confirm) {
          errors.join_code = 'Provided Join Codes do not match.';
        }

        if (
          values.join_code &&
          values.join_code === values.join_code_confirm &&
          !isValidJoinCode(values.join_code)
        ) {
          errors.join_code = 'The join code provided is not valid.';
        }

        if (values.public_join_code_verification_enabled && !values.join_code) {
          errors.public_join_code_verification_enabled =
            'Please add a Join Code or disable "Require Join Code to view".';
        }

        if (errors.join_code) {
          // Returns existing errors without validation.
          return errors;
        }

        if (values.join_code) {
          try {
            const {
              data: { joinCodeByCode },
            } = await apolloClient.query({
              query: joinCodeSearch,
              variables: { code: values.join_code },
            });

            if (joinCodeByCode?.album?.id || joinCodeByCode?.album_folder?.id) {
              errors.join_code =
                'Provided Join Code already exist. Please select a different one.';
            }
          } catch (err) {
            errors.join_code = getErrorString(err);
            return errors;
          }
        }

        return errors;
      },
      skippable: () => {
        return true;
      },
      onSkip: ({ handleChange }) => {
        handleChange({
          target: {
            name: 'join_code',
            value: '',
          },
        });
        handleChange({
          target: {
            name: 'join_code_confirm',
            value: '',
          },
        });
        handleChange({
          target: {
            name: 'public_join_code_verification_enabled',
            value: false,
          },
        });
      },
    },
    [FREEMIUM_EVENT_TUTORIAL_STEPS.MEMORY_BOOK_SETUP]: {
      title: 'Walk down memory lane with your Memories Smart Slideshow!',
      key: FREEMIUM_EVENT_TUTORIAL_STEPS.MEMORY_BOOK_SETUP,
      alertLabel: 'Walk down memory lane with your Memories Smart Slideshow!',
      skipBtnLabel: memoryBookPurchased ? 'All done' : 'Skip',
      actionBtnLabel: memoryBookPurchased ? null : 'Add now',
      alertActionBtnProps: {
        id: 'onboarding-mss-banner-action',
      },
      alertSkipBtnProps: {
        id: 'onboarding-mss-banner-skip',
      },
      modalActionBtnProps: {
        id: 'onboarding-mss-action',
      },
      modalSkipBtnProps: {
        id: 'onboarding-mss-skip',
      },
      modalActionBtnLabel: memoryBookPurchased ? (
        'Done'
      ) : (
        <>
          <span className="mr-1">Add now</span>
          <PriceDisplay
            price={memoryBookEventAddon?.price?.value}
            currency={memoryBookEventAddon?.price?.currency}
            trimDecimalZeros={false}
          />
        </>
      ),
      modalSkipBtnLabel: memoryBookPurchased
        ? "I'm done"
        : "I'm not interested",
      layoutSize: 'lg',
      skip: entity?.purchased_event_addon_types?.includes(
        EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
      ),
      component: props => {
        return (
          <>
            <FreemiumEventTutorialMemoryBookStep
              stepProps={stepProps}
              setStepProps={setStepProps}
              {...props}
            />
          </>
        );
      },
    },
    [FREEMIUM_EVENT_TUTORIAL_STEPS.SOCIAL]: {
      label: 'Social',
      key: FREEMIUM_EVENT_TUTORIAL_STEPS.SOCIAL,
      title: 'Add a customizable photo overlay and hashtag for social shares',
      alertLabel:
        'Add a customizable photo overlay and hashtag for social shares',
      skipBtnLabel: 'Skip',
      actionBtnLabel: 'Add now',
      modalActionBtnLabel: 'Continue',
      modalSkipBtnLabel: 'Skip',
      layoutSize: 'lg',
      skippable: () => {
        return true;
      },
      skip: !entity?.purchased_event_addon_types?.includes(
        EVENT_ADDON_ITEM_TYPE.WALDOFIED,
      ),
      component: props => {
        return (
          <>
            <FreemiumEventCreateSocialStep
              stepProps={stepProps}
              setStepProps={setStepProps}
              {...props}
            />
          </>
        );
      },
      validate: values => {
        const errors = {};
        if (
          values.watermark_config?.asset_image?.id &&
          !values.watermark_config?.placement
        ) {
          errors.watermark_config =
            'Please select your photo overlay placement';
        }

        return errors;
      },
      onSkip: ({ handleChange }) => {
        handleChange({
          target: {
            name: 'hash_code',
            value: '',
          },
        });
        handleChange({
          target: {
            name: 'watermark_config',
            value: {},
          },
        });
      },
    },
    [FREEMIUM_EVENT_TUTORIAL_STEPS.INVITE]: {
      label: 'Review',
      key: FREEMIUM_EVENT_TUTORIAL_STEPS.INVITE,
      title: 'Send your invites and get the party started!',
      alertLabel: 'Invite your guests and get the party started!',
      skipBtnLabel: 'Maybe later',
      actionBtnLabel: 'Invite others',
      modalActionBtnLabel: "I'm done!",
      modalActionBtnProps: {
        className: 'freemium-event-tutorial-action-btn d-none',
      },
      modalSkipBtnLabel: 'Maybe later',
      modalSkipBtnProps: {
        className: 'mx-auto mt-0 d-flex skip-button text-bold',
      },
      layoutSize: 'lg',
      skip: false,
      skippable: () => {
        return true;
      },
      onSkip: () => {},
      component: ({ handleSubmit }) => {
        return (
          <>
            <p className="text-center">
              Invite your guests with your private share link.
            </p>
            <ShutterbugShare
              albumFolder={
                isAlbumFolderEntity ? entity : entity.root_album_folder
              }
              album={!isAlbumFolderEntity ? entity : undefined}
              className="freemium-event-tutorial-share-step"
              withSaveTheDateSetup={false}
              onRequestClose={handleSubmit}
              headerless
            />
          </>
        );
      },
      validate: () => {},
    },
  };
};

export const processTutorialStep = async ({
  user,
  entity,
  values,
  steps,
  openModal,
  closeModal,
  activeStep,
  albumCreate,
  albumUpdate,
  albumFolderUpdate,
  onEventAddonPurchaseCheckout,
  purchasedAddonItems,
  eventAddonItems,
  onCheckoutSuccess,
  isNativeWebView,
}) => {
  const { key } = activeStep;

  const isAlbumFolderBool = isAlbumFolder(entity);
  const isSpecialtyAlbumBool = isSpecialtyAlbum(entity);

  const albumFolderId = isAlbumFolderBool
    ? entity.id
    : entity.root_album_folder?.id;

  const [groupVideoEventAddon] = getEventAddonItemsByType(
    eventAddonItems,
    EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
  );
  const [memoryBookEventAddon] = getEventAddonItemsByType(
    eventAddonItems,
    EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
  );
  const [waldofiedEventAddon] = getEventAddonItemsByType(
    eventAddonItems,
    EVENT_ADDON_ITEM_TYPE.WALDOFIED,
  );

  switch (key) {
    case FREEMIUM_EVENT_TUTORIAL_STEPS.PHOTO_ALBUMS: {
      const customAlbums = values.albums.filter(
        albumItem => albumItem.selected && !albumItem.is_wedding_day_album,
      );

      if (customAlbums.length > 0) {
        await Promise.all(
          customAlbums.map(async customAlbum => {
            const {
              data: {
                albumCreate: { album: createdAlbum },
              },
            } = await albumCreate({
              variables: {
                input: {
                  name: customAlbum.name,
                  parent_album_folder_id: albumFolderId,
                  creator_id: user?.id,
                  password: values.password,
                  invite_only_access: values.invite_only_access,
                  privacy: values.privacy,
                  album_type: ALBUM_TYPE.EVENT,
                  album_subtype: ALBUM_SUBTYPE.WEDDINGS,
                  join_code: generateMachineJoinCode(),
                  display_order: customAlbum.display_order,
                  enable_external_upload:
                    values.permission === ALBUM_UPLOAD_PERMISSIONS.ANYONE,
                  external_uploads_require_review: values.require_review,
                },
              },
            });

            return createdAlbum;
          }),
        );
      }

      onCheckoutSuccess?.();

      return { stepComplete: true };
    }
    case FREEMIUM_EVENT_TUTORIAL_STEPS.QR_CODE_DOWNLOAD: {
      if (
        purchasedAddonItems.find(
          purchasedEventAddon =>
            purchasedEventAddon.event_addon_type ===
            EVENT_ADDON_ITEM_TYPE.WALDOFIED,
        )
      ) {
        return { stepComplete: true };
      }

      if (isNativeWebView) {
        postNativeAppFreemiumEventTutorialConfigEventAddonItemPurchase({
          entityId: entity?.id,
          eventAddonItemId: waldofiedEventAddon?.id,
          eventAddonItemType: EVENT_ADDON_ITEM_TYPE.WALDOFIED,
        });

        return { stepComplete: false };
      }

      openModal(STRIPE_CHECKOUT_MODAL, {
        buttonLabel: 'Complete payment',
        // If the event is FREE, will not require CC
        cardRequired: false,
        onSuccess: token => {
          onEventAddonPurchaseCheckout({
            token,
            albumFolderId,
            values,
            steps,
            eventAddonItemIds: [waldofiedEventAddon.id],
          });
        },
        accountReferralTypes: [ACCOUNT_REFERRAL_TYPE.EVENTS],
        checkoutPriceVariables: ({ promo_code: promoCode }) => {
          return {
            event_addon_items: [
              {
                event_addon_item_id: waldofiedEventAddon.id,
                quantity: 1,
                album_folder_id: albumFolderId,
              },
            ],
            promo_code: promoCode,
          };
        },
        onClose: () => {
          closeModal(STRIPE_CHECKOUT_MODAL);
        },
        defaultName: user?.name ?? '',
        defaultEmail: user?.email_address ?? '',
      });

      return { stepComplete: false };
    }
    case FREEMIUM_EVENT_TUTORIAL_STEPS.GROUP_VIDEO_SETUP: {
      if (
        purchasedAddonItems.find(
          purchasedEventAddon =>
            purchasedEventAddon.event_addon_type ===
            EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
        )
      ) {
        return { stepComplete: true };
      }

      if (isNativeWebView) {
        postNativeAppFreemiumEventTutorialConfigEventAddonItemPurchase({
          entityId: entity?.id,
          eventAddonItemId: groupVideoEventAddon?.id,
          eventAddonItemType: EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
        });

        return { stepComplete: false };
      }

      openModal(STRIPE_CHECKOUT_MODAL, {
        buttonLabel: 'Complete payment',
        // If the event is FREE, will not require CC
        cardRequired: false,
        onSuccess: token => {
          onEventAddonPurchaseCheckout({
            token,
            albumFolderId,
            values,
            steps,
            eventAddonItemIds: [groupVideoEventAddon.id],
          });
        },
        accountReferralTypes: [ACCOUNT_REFERRAL_TYPE.EVENTS],
        checkoutPriceVariables: ({ promo_code: promoCode }) => {
          return {
            event_addon_items: [
              {
                event_addon_item_id: groupVideoEventAddon.id,
                quantity: 1,
                album_folder_id: albumFolderId,
              },
            ],
            promo_code: promoCode,
          };
        },
        onClose: () => {
          closeModal(STRIPE_CHECKOUT_MODAL);
        },
        defaultName: user?.name ?? '',
        defaultEmail: user?.email_address ?? '',
      });

      return { stepComplete: false };
    }

    case FREEMIUM_EVENT_TUTORIAL_STEPS.MEMORY_BOOK_SETUP: {
      if (
        purchasedAddonItems.find(
          purchasedEventAddon =>
            purchasedEventAddon.event_addon_type ===
            EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
        )
      ) {
        return { stepComplete: true };
      }

      if (isNativeWebView) {
        postNativeAppFreemiumEventTutorialConfigEventAddonItemPurchase({
          entityId: entity?.id,
          eventAddonItemId: memoryBookEventAddon?.id,
          eventAddonItemType: EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
        });

        return { stepComplete: false };
      }

      openModal(STRIPE_CHECKOUT_MODAL, {
        buttonLabel: 'Complete payment',
        // If the event is FREE, will not require CC
        cardRequired: false,
        onSuccess: token => {
          onEventAddonPurchaseCheckout({
            token,
            albumFolderId,
            values,
            steps,
            eventAddonItemIds: [memoryBookEventAddon.id],
          });
        },
        accountReferralTypes: [ACCOUNT_REFERRAL_TYPE.EVENTS],
        checkoutPriceVariables: ({ promo_code: promoCode }) => {
          return {
            event_addon_items: [
              {
                event_addon_item_id: memoryBookEventAddon.id,
                quantity: 1,
                album_folder_id: albumFolderId,
              },
            ],
            promo_code: promoCode,
          };
        },
        onClose: () => {
          closeModal(STRIPE_CHECKOUT_MODAL);
        },
        defaultName: user?.name ?? '',
        defaultEmail: user?.email_address ?? '',
      });

      return { stepComplete: false };
    }

    case FREEMIUM_EVENT_TUTORIAL_STEPS.PERMISSIONS: {
      const data = {
        ...pick(values, ['password', 'privacy', 'invite_only_access']),
        auto_contributor_settings:
          values.permission === ALBUM_UPLOAD_PERMISSIONS.ANYONE
            ? {
                upload_access: values.require_review
                  ? CONTRIBUTOR_UPLOAD_ACCESS.REVIEW
                  : CONTRIBUTOR_UPLOAD_ACCESS.PUBLISH,
                view_access: isSpecialtyAlbumBool
                  ? CONTRIBUTOR_VIEW_ACCESS.OWN
                  : CONTRIBUTOR_VIEW_ACCESS.ALL,
              }
            : {
                upload_access: CONTRIBUTOR_UPLOAD_ACCESS.NONE,
                view_access: isSpecialtyAlbumBool
                  ? CONTRIBUTOR_VIEW_ACCESS.OWN
                  : CONTRIBUTOR_VIEW_ACCESS.ALL,
              },
        enable_external_upload:
          values.permission === ALBUM_UPLOAD_PERMISSIONS.ANYONE,
        external_uploads_require_review: values.require_review,
        member_uploads_require_review: values.require_review,
      };
      if (isAlbumFolderBool) {
        await albumFolderUpdate({
          variables: {
            input: {
              album_folder_id: entity?.album_folder?.id ?? entity?.id,
              ...data,
            },
          },
        });
      } else {
        await albumUpdate({
          variables: {
            input: {
              id: entity?.id,
              ...data,
            },
          },
        });
      }

      return { stepComplete: true };
    }
    case FREEMIUM_EVENT_TUTORIAL_STEPS.JOIN_CODE: {
      if (values.join_code) {
        await albumFolderUpdate({
          variables: {
            input: {
              album_folder_id: albumFolderId,
              join_code: values.join_code,
            },
          },
        });
      }

      return { stepComplete: true };
    }
    case FREEMIUM_EVENT_TUTORIAL_STEPS.SOCIAL: {
      await albumFolderUpdate({
        variables: {
          input: {
            album_folder_id: albumFolderId,
            hash_code: values.hash_code,
            watermark: getWatermarkConfigInput(values.watermark_config),
          },
        },
      });

      return { stepComplete: true };
    }

    default: {
      return { stepComplete: true };
    }
  }
};
